// TubeRender: the train canvas over the map. Tests drive it in Node with
// stand-in canvases and a stand-in map.
//
// Everything drawn comes from scene(), which returns
//   { net, trains, layouts, hidden, focus, selectedId, visibleStations }
// trains: tracking id -> tracked train (TubeCore.mergeTrains). The renderer
// reads t.pred and t.motion and keeps its own per-train state in t.render.
// layouts: line id -> TubeCore.linePaths(net, id, true).
// visibleStations: ids of stations on at least one shown line.
//
// Zooms are in Leaflet's scale (the world is 256 * 2^z px wide), which every
// size here was tuned in; MapLibre's zoom is one less (see view.ts).
//
// Tracks are projected to world pixels once, at zoom 0, and cached per
// network layout. Each frame scales them to the current zoom and offsets the
// rails in drawn pixels (TubeCore.TrackView), so a zoom animation never
// projects anything again.

import * as Core from './core.ts';
import { MINOR, isMinor } from './minor.ts';
import type {
  DisplayPosition,
  LatLngTuple,
  LineId,
  LineStyle,
  Network,
  ProjectedTrack,
  Scene,
  ScreenPoint,
  Station,
  StationId,
  TrackedTrain,
  TrackView,
} from './types.ts';

// Shared with MapLibre (map-frame.ts): avoid DPR-3 raster cost on phones.
export const MAX_PIXEL_RATIO = 2;

// The parts of each dependency the renderer uses, so tests can pass plain
// stand-ins.

// The map, as the renderer sees it. view.ts provides it for MapLibre.
export interface RenderMap {
  // The current zoom, in Leaflet's scale (a 256 px world at zoom 0).
  readonly getZoom: () => number;
  // The container's size in CSS pixels.
  readonly getSize: () => ScreenPoint;
  // The world pixel, at getZoom(), of the container's top-left corner.
  readonly getOrigin: () => ScreenPoint;
  // A latitude and longitude's world pixel at zoom 0.
  readonly project: (lat: number, lng: number) => ScreenPoint;
  // Re-read the container's size (after a tab comes back).
  readonly resize: () => void;
}

export interface Canvas2D {
  globalAlpha: number;
  lineWidth: number;
  lineCap: CanvasLineCap;
  lineJoin: CanvasLineJoin;
  fillStyle: string | CanvasGradient | CanvasPattern;
  strokeStyle: string | CanvasGradient | CanvasPattern;
  font: string;
  textAlign: CanvasTextAlign;
  textBaseline: CanvasTextBaseline;
  fillText(text: string, x: number, y: number): void;
  // A browser's canvas measures text; stand-ins may leave it out (minor
  // vehicles' tags then estimate their width).
  measureText?(text: string): { readonly width: number };
  setTransform(a: number, b: number, c: number, d: number, e: number, f: number): void;
  clearRect(x: number, y: number, w: number, h: number): void;
  fillRect(x: number, y: number, w: number, h: number): void;
  createRadialGradient(x0: number, y0: number, r0: number, x1: number, y1: number, r1: number): CanvasGradient;
  createLinearGradient(x0: number, y0: number, x1: number, y1: number): CanvasGradient;
  // A sprite canvas here; typed loosely so the real context's drawImage fits.
  drawImage(image: object, dx: number, dy: number, dw: number, dh: number): void;
  save(): void;
  restore(): void;
  translate(x: number, y: number): void;
  rotate(angle: number): void;
  beginPath(): void;
  closePath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  arc(x: number, y: number, radius: number, startAngle: number, endAngle: number): void;
  fill(): void;
  stroke(): void;
}

// An offscreen canvas for glow sprites (createCanvas).
export interface SpriteCanvas {
  width: number;
  height: number;
  readonly getContext: (contextId: '2d') => Canvas2D | null;
}

// A canvas laid over the map.
export interface OverlayCanvas extends SpriteCanvas {
  readonly style: { width: string; height: string };
}

// Line id -> style. The renderer reads the on-map color, a bullet ("A",
// "6") to draw on the line's trains zoomed in (New York's; London's lines
// have none), and whether the line is minor (minor.ts: its vehicles are dots,
// only zoomed in).
export type RenderLines = Readonly<
  Partial<
    Record<
      LineId,
      Pick<LineStyle, 'show'> & { readonly bullet?: string; readonly minor?: boolean; readonly system?: string }
    >
  >
>;

export interface RenderOptions {
  readonly map: RenderMap;
  readonly lines: RenderLines;
  readonly trainCanvas: OverlayCanvas;
  readonly scene: () => Scene;
  // Draw station dots with the trains (default). Off when the map draws them
  // itself: the rings around a station with a train stopped at it are still
  // drawn, over the map's dots.
  readonly stationDots?: boolean;
  // Which track a train rides once lines split in two: the left of its
  // heading (default; Britain, the Tube) or the right (American railways,
  // New York's subway and railroads). Or per line, for a map of several
  // countries (apps/world).
  readonly keepRight?: boolean | ((lineId: LineId) => boolean);
  readonly reduceMotion?: boolean;
  readonly debug?: boolean;
  readonly isHidden?: () => boolean;
  readonly requestFrame?: (fn: () => void) => void;
  readonly clock?: () => number;
  readonly createCanvas?: () => SpriteCanvas;
}

export interface Renderer {
  readonly resize: () => void;
  readonly draw: (dt?: number) => void;
  readonly pickTrain: (pt: ScreenPoint, radius?: number) => TrackedTrain | null;
  readonly resume: () => void;
}

const INK = '#0A1230';
// A light outline for lines too dark to see on the map (the Northern line's
// black): around their tracks, train pills and station rings.
const CASING = '#D8DCEA';
// Whether a color is too dark to see on INK without CASING.
function isDark(hex: string): boolean {
  const n = parseInt(hex.slice(1), 16);
  return (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255 < 0.12;
}
// Zoom at which each line splits into two tracks.
const SPLIT_Z = 13;
const SPLIT_FROM = SPLIT_Z - 0.35;
const SPLIT_FULL = SPLIT_Z + 0.35;
// Presentation only: Leaflet 10 is MapLibre 9. Keep feeds and selection live.
const VEHICLE_Z = 10;
// Zoom from which trains carry their line's bullet.
const BULLET_Z = 13.5;
// A bullet's smallest type, px (placeBullets).
const BULLET_FONT_MIN = 10;
const clamp = (v: number, a: number, b: number): number => Math.max(a, Math.min(b, v));

// A minor vehicle's tag's type (render's placeTags).
const tagFont = (px: number): string => `700 ${px}px "Hanken Grotesk", system-ui, sans-serif`;

function trainScale(z: number): number {
  return clamp(0.62 + (z - 10) * 0.2, 0.55, 1.55);
}

// Half the distance between a line's two tracks, in screen pixels. Sized so a
// train pill sits on its own track without covering the other one.
function railGap(z: number): number {
  return (5.2 * trainScale(z)) / 2 + 0.6;
}

function splitProgress(z: number): number {
  return clamp((z - SPLIT_FROM) / (SPLIT_FULL - SPLIT_FROM), 0, 1);
}

// Station dot radius in px, and the width of its outline. Kept small so a
// dot marks the station without hiding the trains and tracks around it.
function stationRadius(z: number, inter: boolean): number {
  const r = clamp(2.2 + (z - 11) * 0.55, 2.2, 4.4);
  return inter ? r * 1.25 : r;
}
function stationLine(z: number): number {
  return clamp(1.2 + (z - 11) * 0.2, 1.2, 1.8);
}

// Two or more of the page's major lines call here: minor lines (buses)
// don't make an interchange.
function isInterchange(st: Pick<Station, 'lines'>, lines: RenderLines): boolean {
  let n = 0;
  for (const id of st.lines) if (lines[id] && !isMinor(lines, id)) n++;
  return n > 1;
}

function hexA(hex: string, a: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

// getContext('2d') only fails for a canvas already given another context type.
function context2d(canvas: Readonly<SpriteCanvas>): Canvas2D {
  const g = canvas.getContext('2d');
  if (!g) throw new TypeError('no 2D canvas context');
  return g;
}

// A train stopped at a station: parked there (f = 1) or waiting at its
// first stop (no previous station).
const atStation = (pos: DisplayPosition): boolean => pos.P === null || pos.P === '' || pos.f >= 0.999;

// Whether a font is loaded (always, without a document's font list).
function fontReady(font: string): boolean {
  if (typeof document === 'undefined' || document.fonts === undefined) return true;
  return document.fonts.check(font);
}

function noNetwork(): never {
  throw new TypeError('no network');
}

// How far outside the canvas (px) a train's display position may be and still
// be placed and drawn: its pill, glow and trail reach about 30 px.
const CULL = 80;

// A train that snapped, fading out where it was drawn (world pixels at zoom 0).
// ms a snap's crossfade takes: the old pill fades out and the new one in
// over the same time, linearly, so together they stay about as bright as one
// train. (The new one used to ease in over 500 ms while the old one went in
// 350, which left it at half opacity, a blink, when the old one vanished.)
const SNAP_FADE_MS = 350;
// Trains drawn fainter than this can't be picked.
const PICK_ALPHA = 0.1;

interface Ghost extends ScreenPoint {
  readonly ang: number | null | undefined;
  a: number;
  readonly color: string;
  readonly lineId: LineId;
  readonly hollow: boolean;
}

// A scheduled train (TrainPrediction.scheduled: no live data, placed by the
// timetable) is drawn hollow: its line's color as a ring around a dark core,
// with no glow, so it reads as the line's train but not a live one. The
// ring's share of the pill's width, and its thinnest, px.
const HOLLOW_RING = 0.28;
const HOLLOW_RING_MIN = 1.1;
// What's drawn in a line's color on a hollow train's dark core: the color,
// or the casing for a line too dark to see there.
function hollowInk(color: string): string {
  return isDark(color) ? CASING : color;
}

// A channel half way to white, as two hex digits.
const halfWhite = (v: number): string =>
  Math.round(v + (255 - v) * 0.5)
    .toString(16)
    .padStart(2, '0');

// A hollow (scheduled) tag's number on ink: its line's color half way to
// white. Small red type on near-black read as a smudge, though its ratio
// passed.
function hollowText(color: string): string {
  if (isDark(color)) return CASING;
  const n = parseInt(color.slice(1), 16);
  return `#${halfWhite((n >> 16) & 255)}${halfWhite((n >> 8) & 255)}${halfWhite(n & 255)}`;
}

function create(options: RenderOptions): Renderer {
  const {
    map,
    lines,
    trainCanvas,
    scene,
    stationDots = true,
    keepRight = false,
    debug = false,
    isHidden = () => document.hidden,
    requestFrame = (fn: () => void) => requestAnimationFrame(fn),
    clock = () => performance.now(),
    createCanvas = () => document.createElement('canvas'),
  } = options;
  const ctx = context2d(trainCanvas);
  const bulletOf = (lineId: LineId): string | undefined => lines[lineId]?.bullet;
  // The on-map color of a train's line.
  const show = (lineId: LineId): string => {
    const style = lines[lineId];
    if (!style) throw new TypeError(`no style for line ${lineId}`);
    return style.show;
  };
  let W = 0;
  let H = 0;
  let dpr = 1;

  function resize(): void {
    const s = map.getSize();
    const was = dpr;
    dpr = Math.min((typeof devicePixelRatio === 'number' && devicePixelRatio) || 1, MAX_PIXEL_RATIO);
    if (dpr !== was) tagSprites.clear();
    W = s.x;
    H = s.y;
    trainCanvas.width = Math.round(W * dpr);
    trainCanvas.height = Math.round(H * dpr);
    trainCanvas.style.width = `${W}px`;
    trainCanvas.style.height = `${H}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  // ---- trains

  const sprites: Partial<Record<string, SpriteCanvas>> = {};
  function sprite(color: string): SpriteCanvas {
    const cached = sprites[color];
    if (cached !== undefined) return cached;
    const c = createCanvas();
    c.width = c.height = 64;
    const g = context2d(c);
    const grad = g.createRadialGradient(32, 32, 0, 32, 32, 32);
    grad.addColorStop(0, hexA(color, 0.85));
    grad.addColorStop(0.28, hexA(color, 0.42));
    grad.addColorStop(1, hexA(color, 0));
    g.fillStyle = grad;
    g.fillRect(0, 0, 64, 64);
    return (sprites[color] = c);
  }

  // Pill centered on the origin, pointing along +x. Rounded tail, tapered nose.
  // Without a known heading it's a plain pill.
  function trainPath(len: number, wid: number, pointed: boolean): void {
    const r = wid / 2;
    const back = -len / 2 + r;
    const nose = pointed ? wid * 0.8 : 0;
    const front = len / 2 - (pointed ? nose : r);
    ctx.beginPath();
    ctx.moveTo(back, -r);
    ctx.lineTo(front, -r);
    if (pointed) ctx.lineTo(len / 2, 0);
    else ctx.arc(front, 0, r, -Math.PI / 2, Math.PI / 2);
    ctx.lineTo(pointed ? front : back, r);
    if (pointed) ctx.lineTo(back, r);
    ctx.arc(back, 0, r, Math.PI / 2, (3 * Math.PI) / 2);
    ctx.closePath();
  }

  // Dark chevron on pale pills (Northern, Waterloo & City...), white otherwise.
  const chevCache: Partial<Record<string, string>> = {};
  function chevronColor(hex: string): string {
    const cached = chevCache[hex];
    if (cached !== undefined) return cached;
    const n = parseInt(hex.slice(1), 16);
    const y = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
    return (chevCache[hex] = y > 0.62 ? INK : '#FFFFFF');
  }

  // Projected tracks (world pixels at zoom 0), per network layout. Zoom only
  // changes how they're drawn.
  const tracks = new Map<string, ProjectedTrack>();
  let tracksLayouts: Scene['layouts'] | null = null;

  // Where a train is on screen at zoom z, following the drawn (offset) rail.
  // Sets the train's screen heading and trail position in t.render.
  function screenPoint(
    sc: Scene,
    t: TrackedTrain,
    pos: DisplayPosition,
    scale: number,
    railOffset: number,
    origin: ScreenPoint,
    rough: ScreenPoint,
  ): ScreenPoint {
    if (sc.layouts !== tracksLayouts) {
      tracks.clear();
      tracksLayouts = sc.layouts;
    }
    const lineId = t.pred.lineId;
    const r = t.render;
    const next = t.pred.stops[1];
    r.tail = null;
    const hasPrev = pos.P !== null && pos.P !== '';
    const a = hasPrev ? pos.P : pos.N;
    const b = hasPrev ? pos.N : next?.sid;
    const hasNext = b !== undefined && b !== '';
    const net = hasNext ? (sc.net ?? noNetwork()) : null;
    let point: ScreenPoint;
    if (hasNext && net !== null && net.stations.has(b)) {
      const layout = sc.layouts.get(lineId);
      const segment = layout?.segments.get(a + '|' + b);
      const reverse = segment !== undefined && segment.start > segment.end;
      const key = lineId + '|' + (segment ? `${segment.path}` : a + '|' + b);
      let track = tracks.get(key);
      if (!track) {
        const path: readonly LatLngTuple[] | undefined = segment
          ? layout?.paths[segment.path]
          : Core.segPoints(net, lineId, a, b);
        if (!path) throw new TypeError(`no path for ${key}`);
        track = Core.projectedTrack(path.map(([lat, lng]) => map.project(lat, lng)));
        tracks.set(key, track);
      }
      // Trains keep left: the rail left of the path for a train running along
      // it, the right one for a train running against it (and the other way
      // round with keepRight).
      const side = reverse === (typeof keepRight === 'function' ? keepRight(lineId) : keepRight) ? 1 : -1;
      // A minor line is one line at every zoom (minor.ts).
      const gap = isMinor(lines, lineId) ? 0 : railOffset * side;
      const view: TrackView = { k: scale, gap };
      const f = hasPrev ? pos.f : 0;
      // A missing index reads as undefined, which the math below turns into
      // NaN and trackPoint's defaults turn into the track's ends.
      const cs = segment ? (track.cum[segment.start] ?? Number.NaN) : 0;
      const ce = segment ? (track.cum[segment.end] ?? Number.NaN) : 0;
      const distance = segment ? cs + (ce - cs) * f : track.len * f;
      const onTrack = Core.trackPoint(
        track,
        track.len ? distance / track.len : 0,
        segment ? (track.cum[Math.min(segment.start, segment.end)] ?? 0) : 0,
        segment ? (track.cum[Math.max(segment.start, segment.end)] ?? track.len) : track.len,
        view,
      );
      point = onTrack;
      r.ang = onTrack.ang + (reverse ? Math.PI : 0);
      if (hasPrev) {
        r.tail = {
          track,
          at: distance,
          dir: segment ? Math.sign(ce - cs) || 1 : 1,
          view,
        };
      }
    } else {
      point = { x: rough.x * scale, y: rough.y * scale };
      r.ang = null;
    }
    // Unrounded, for continuous movement, including while the map pans.
    return { x: point.x - origin.x, y: point.y - origin.y };
  }

  // stopped: no direction arrow, and a thin white outline. hollow: a
  // scheduled train (HOLLOW_RING).
  function drawPill(
    x: number,
    y: number,
    ang: number | null | undefined,
    a: number,
    color: string,
    len: number,
    wid: number,
    s: number,
    stopped: boolean,
    hollow: boolean,
    bullet?: Tag,
  ): void {
    ctx.globalAlpha = a;
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(ang === null || ang === undefined || Number.isNaN(ang) ? 0 : ang);
    const plen = ang === null || ang === undefined ? wid : len;
    trainPath(plen, wid, false);
    ctx.lineWidth = 3.2;
    ctx.lineJoin = 'round';
    ctx.strokeStyle = isDark(color) ? CASING : INK;
    ctx.stroke();
    ctx.fillStyle = hollow ? hollowInk(color) : color;
    ctx.fill();
    if (hollow) {
      const ring = Math.max(HOLLOW_RING_MIN, wid * HOLLOW_RING);
      trainPath(plen - 2 * ring, wid - 2 * ring, false);
      ctx.fillStyle = INK;
      ctx.fill();
      trainPath(plen, wid, false);
    }
    if (stopped) {
      ctx.lineWidth = Math.max(1, 1.1 * s);
      ctx.strokeStyle = '#FFFFFF';
      ctx.stroke();
    } else if (ang !== null && ang !== undefined) {
      const cw = wid * 0.27;
      const cl = len * 0.12;
      ctx.beginPath();
      ctx.moveTo(-cl, -cw);
      ctx.lineTo(cl, 0);
      ctx.lineTo(-cl, cw);
      ctx.lineWidth = Math.max(1.2, 1.35 * s);
      ctx.lineCap = 'round';
      ctx.strokeStyle = hollow ? hollowInk(color) : chevronColor(color);
      ctx.stroke();
    }
    ctx.restore();
    if (bullet) drawTag(x, y, a, color, bullet, stopped ? '#FFFFFF' : undefined, hollow);
  }

  // A minor vehicle's tag: its route's number, in type sized to be read.
  interface Tag {
    readonly text: string;
    readonly font: string;
    readonly w: number;
    readonly h: number;
  }
  // Without measureText (a test's stand-in canvas): Hanken Grotesk's bold
  // digits and capitals are about 0.62 em.
  function textWidth(text: string, font: string, px: number): number {
    if (!ctx.measureText) return text.length * px * 0.62;
    ctx.font = font;
    return ctx.measureText(text).width;
  }

  // Which minor vehicles get a tag: each in turn, unless its tag would cover
  // one already placed (a vehicle's order is the scene's, so the same bus
  // keeps its tag from frame to frame).
  function placeTags(
    minor: readonly { readonly t: TrackedTrain; readonly p: ScreenPoint; readonly a: number }[],
    z: number,
  ): Map<TrackedTrain, Tag> {
    const px = Math.round(clamp(MINOR.LABEL_FONT + (z - MINOR.BULLET_Z), MINOR.LABEL_FONT, MINOR.LABEL_FONT_MAX));
    const font = tagFont(px);
    const h = Math.round(px * 1.45);
    const placed: { readonly x: number; readonly y: number; readonly w: number }[] = [];
    const out = new Map<TrackedTrain, Tag>();
    for (const { t, p, a } of minor) {
      const text = bulletOf(t.pred.lineId);
      if (a < 0.02 || text === undefined || text === '' || text.length > MINOR.LABEL_MAX_CHARS) continue;
      const w = Math.max(h, textWidth(text, font, px) + px * 0.9);
      // Touching, or overlapping by 2 px, is fine; more than that isn't.
      const hit = placed.some((q) => Math.abs(q.x - p.x) < (q.w + w) / 2 - 2 && Math.abs(q.y - p.y) < h - 2);
      if (hit) continue;
      placed.push({ x: p.x, y: p.y, w });
      out.set(t, { text, font, w, h });
    }
    return out;
  }

  // A train's bullet, upright over its pill's middle: its line's letters in
  // type at least BULLET_FONT_MIN px (a disc the pill's width held "504" in
  // 7 px), on a disc, or a lozenge when they're wider than it. Placed like
  // the minor lines' tags: one that would cover a bullet already placed is
  // left off (the pill and its chevron stay), so a bunch of streetcars shows
  // a readable number or two rather than a pile of overlapping ones.
  function placeBullets(
    list: readonly { readonly t: TrackedTrain; readonly p: ScreenPoint; readonly a: number }[],
    wid: number,
  ): Map<TrackedTrain, Tag> {
    const disc = wid * 1.9;
    const placed: { readonly x: number; readonly y: number; readonly w: number; readonly h: number }[] = [];
    const out = new Map<TrackedTrain, Tag>();
    const sized = new Map<string, Tag>();
    for (const { t, p, a } of list) {
      const text = bulletOf(t.pred.lineId);
      if (a < 0.02 || text === undefined || text === '') continue;
      let tag = sized.get(text);
      if (!tag) {
        // Four letters (a bus's "133E") a little smaller than one.
        const share = text.length > 3 ? 0.82 : text.length > 1 ? 1.05 : 1.35;
        const px = Math.round(Math.max((disc / 2) * share, text.length > 2 ? BULLET_FONT_MIN : BULLET_FONT_MIN + 1));
        const font = tagFont(px);
        const h = Math.max(disc, Math.round(px * 1.4));
        tag = { text, font, w: Math.max(h, textWidth(text, font, px) + px * 0.7), h };
        sized.set(text, tag);
      }
      const { w, h } = tag;
      if (placed.some((q) => Math.abs(q.x - p.x) < (q.w + w) / 2 - 2 && Math.abs(q.y - p.y) < (q.h + h) / 2 - 2))
        continue;
      placed.push({ x: p.x, y: p.y, w, h });
      out.set(t, tag);
    }
    return out;
  }

  // A tag: a lozenge in the line's color, ringed in ink (or `ring`), with the
  // route's number in the chevron's color; hollow (a scheduled train's), a
  // dark lozenge ringed and numbered in the line's color.
  function drawTag(x: number, y: number, a: number, color: string, tag: Tag, ring?: string, hollow = false): void {
    const img = tagSprite(tag, color, ring ?? (hollow ? hollowInk(color) : isDark(color) ? CASING : INK), hollow);
    ctx.globalAlpha = a;
    ctx.drawImage(img.canvas, x - img.w / 2, y - img.h / 2, img.w, img.h);
  }

  // A tag, drawn once into its own canvas and placed as one image, so its
  // lozenge and number can't move apart. Drawn straight onto the map, a moving
  // bus's number drifted up to a pixel off its lozenge, then jumped back, over
  // and over: the browser draws a shape wherever it's put, a fraction of a
  // pixel included, but snaps text's baseline to whole device pixels (Chrome
  // and Safari), and Safari snaps an image a little too.
  interface TagSprite {
    readonly canvas: SpriteCanvas;
    readonly w: number;
    readonly h: number;
  }
  const tagSprites = new Map<string, TagSprite>();
  function tagSprite(tag: Tag, color: string, ring: string, hollow: boolean): TagSprite {
    // Quantized to device pixels so an animated zoom reuses sprites instead
    // of rasterizing a new one each frame for a fraction of a pixel.
    const qw = Math.round(tag.w * dpr) / dpr;
    const qh = Math.round(tag.h * dpr) / dpr;
    const key = `${tag.font}|${qw}|${qh}|${color}|${ring}|${hollow ? 'h' : ''}|${tag.text}`;
    const cached = tagSprites.get(key);
    if (cached) return cached;
    // Whole, even CSS pixels with room for the ring, so the tag's middle is
    // the canvas's.
    const w = 2 * Math.ceil(qw / 2) + 4;
    const h = 2 * Math.ceil(qh / 2) + 4;
    const canvas = createCanvas();
    canvas.width = Math.ceil(w * dpr);
    canvas.height = Math.ceil(h * dpr);
    const g = context2d(canvas);
    g.setTransform(canvas.width / w, 0, 0, canvas.height / h, 0, 0);
    const x = w / 2;
    const y = h / 2;
    const r = qh / 2;
    const half = qw / 2 - r;
    g.beginPath();
    g.moveTo(x - half, y - r);
    g.lineTo(x + half, y - r);
    g.arc(x + half, y, r, -Math.PI / 2, Math.PI / 2);
    g.lineTo(x - half, y + r);
    g.arc(x - half, y, r, Math.PI / 2, (Math.PI * 3) / 2);
    g.closePath();
    g.fillStyle = hollow ? INK : color;
    g.fill();
    g.lineWidth = hollow ? 2 : 1.5;
    g.lineJoin = 'round';
    g.strokeStyle = ring;
    g.stroke();
    g.fillStyle = hollow ? hollowText(color) : chevronColor(color);
    g.font = tag.font;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText(tag.text, x, y + r * 0.08);
    const out = { canvas, w, h };
    // Kept once its web font has loaded (before that it's in a fallback).
    // Bounded: a few hundred routes at a few type sizes. Evicted oldest
    // first so one overflow does not drop every cached sprite.
    if (fontReady(tag.font)) {
      if (tagSprites.size >= 1000) {
        const oldest = tagSprites.keys().next().value;
        if (oldest !== undefined) tagSprites.delete(oldest);
      }
      tagSprites.set(key, out);
    }
    return out;
  }

  // Station positions in world pixels at zoom 0, per network.
  const stationPts = new Map<StationId, ScreenPoint>();
  let stationPtsNet: Network | null = null;
  function stationPoint(net: Network, st: Station, scale: number, o: ScreenPoint): ScreenPoint {
    if (net !== stationPtsNet) {
      stationPts.clear();
      stationPtsNet = net;
    }
    let w = stationPts.get(st.id);
    if (!w) stationPts.set(st.id, (w = map.project(st.lat, st.lon)));
    return { x: w.x * scale + o.x, y: w.y * scale + o.y };
  }

  // Station dots (zoom 11+). Interchanges are solid white with an ink ring,
  // like the Tube map; other stations are ink with a white ring.
  function drawDots(sc: Scene, net: Network, z: number, o: ScreenPoint): void {
    const scale = 2 ** z;
    ctx.lineWidth = stationLine(z);
    for (const inter of [false, true]) {
      ctx.beginPath();
      for (const st of net.stations.values()) {
        if (isInterchange(st, lines) !== inter || !sc.visibleStations.has(st.id)) continue;
        const p = stationPoint(net, st, scale, o);
        const r = stationRadius(z, inter);
        if (p.x < -r || p.y < -r || p.x > W + r || p.y > H + r) continue;
        ctx.moveTo(p.x + r, p.y);
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
      }
      ctx.fillStyle = inter ? '#F4F6FC' : INK;
      ctx.strokeStyle = inter ? INK : '#EEF0F8';
      ctx.fill();
      ctx.stroke();
    }
  }

  // When a poll moves a train onto a route that doesn't connect with the one
  // drawn, TubeCore.trainDisplay jumps it and flags `snapped`: crossfade that.
  const ghosts: Ghost[] = [];
  // Clear the last detailed fleet once when entering overview, not every frame.
  let detailedTrains: Scene['trains'] | null = null;

  // dt: ms since the last animation frame, or 0 for a redraw without time
  // passing (camera moves, resizes).
  function draw(dt = 0): void {
    ctx.clearRect(0, 0, W, H);
    const sc = scene();
    const z = map.getZoom();
    if (z < VEHICLE_Z) {
      if (detailedTrains) {
        for (const t of detailedTrains.values()) {
          t.render.px = null;
          t.render.shown = 0;
          t.render.world = undefined;
          t.render.tail = null;
        }
        detailedTrains = null;
      }
      ghosts.length = 0;
      return;
    }
    const net = sc.net;
    if (!net) return;
    detailedTrains = sc.trains;
    const { hidden, focus } = sc;
    const s = trainScale(z);
    const scale = 2 ** z;
    const railOffset = railGap(Math.max(z, SPLIT_FULL)) * splitProgress(z);
    const origin = map.getOrigin();
    // Container point = world pixel at zoom z + o.
    const o = { x: -origin.x, y: -origin.y };
    const list: { t: TrackedTrain; pos: DisplayPosition; p: ScreenPoint; a: number; color: string }[] = [];
    // Minor lines' vehicles: dots, only from MINOR.VEHICLE_Z (minor.ts).
    const minor: { t: TrackedTrain; p: ScreenPoint; a: number; color: string }[] = [];
    const minorShown = z >= MINOR.VEHICLE_Z;
    for (const t of sc.trains.values()) {
      const r = t.render;
      const lineId = t.pred.lineId;
      const moving = t.motion.moving === true;
      r.appear ??= 1;
      if (dt) {
        // Trails grow while a train moves and fade once it stops.
        r.tailK = (r.tailK ?? 0) + ((moving ? 1 : 0) - (r.tailK ?? 0)) * Math.min(1, dt / (moving ? 700 : 1500));
        r.appear = Math.min(1, r.appear + dt / SNAP_FADE_MS);
      }
      r.px = null;
      r.shown = 0;
      const pos = t.motion.pos;
      const isMinorLine = isMinor(lines, lineId);
      if (hidden.has(lineId) || !pos || (isMinorLine && !minorShown)) {
        r.world = undefined;
        r.tail = null;
        continue;
      }
      // Placing a train on its drawn rail is the costly part of a frame, and
      // most trains are off screen when zoomed in. Its display position is on
      // the same track (the rail is a few px to its side), so skip trains
      // well outside the canvas. They leave no ghost if they snap there.
      const rough = Core.projectTrain(t, pos, map.project);
      const rx = rough.x * scale - origin.x;
      const ry = rough.y * scale - origin.y;
      if (rx < -CULL || ry < -CULL || rx > W + CULL || ry > H + CULL) {
        r.world = undefined;
        r.tail = null;
        continue;
      }
      const p = screenPoint(sc, t, pos, scale, railOffset, origin, rough);
      const world = { x: (p.x - o.x) / scale, y: (p.y - o.y) / scale };
      // A snap shorter than a pill on screen (a small route correction,
      // zoomed out) just moves the train: a crossfade there dims it in place.
      const jump = r.world ? Math.hypot(r.world.x - world.x, r.world.y - world.y) * scale : 0;
      if (dt && r.world && !isMinorLine && t.alpha * r.appear > 0.3 && pos.snapped !== false && jump >= 15 * s) {
        ghosts.push({
          ...r.world,
          ang: r.worldAng,
          a: t.alpha * r.appear,
          color: show(lineId),
          lineId,
          hollow: t.pred.scheduled === true,
        });
        r.appear = 0;
        r.tailK = 0;
      }
      r.world = world;
      r.worldAng = r.ang;
      if (p.x < -30 || p.y < -30 || p.x > W + 30 || p.y > H + 30) continue;
      const dim = focus !== null && focus !== '' && focus !== lineId ? 0.12 : 1;
      r.px = p;
      r.shown = t.alpha * r.appear;
      if (isMinorLine) minor.push({ t, p, a: t.alpha * r.appear * dim, color: show(lineId) });
      else list.push({ t, pos, p, a: t.alpha * r.appear * dim, color: show(lineId) });
    }

    const len = 15 * s;
    const wid = (6.4 - 1.2 * splitProgress(z)) * s;

    // Trails: a fading streak along the track behind each moving train.
    const tailLen = len * 2.6;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = wid * 0.7;
    for (const { t, a, color } of list) {
      const { tail, tailK } = t.render;
      if (a < 0.02 || !tail || tailK === undefined || !(tailK > 0.02)) continue;
      const back = (tailLen * tailK) / tail.view.k;
      const pts = Core.trackSlice(tail.track, tail.at, tail.at - tail.dir * back, tail.view);
      // trackSlice always returns both ends.
      const head = pts[0];
      const end = pts.at(-1);
      if (!head || !end || Math.hypot(end.x - head.x, end.y - head.y) < 1) continue;
      const g = ctx.createLinearGradient(head.x + o.x, head.y + o.y, end.x + o.x, end.y + o.y);
      g.addColorStop(0, hexA(color, 0.55));
      g.addColorStop(1, hexA(color, 0));
      ctx.globalAlpha = a;
      ctx.strokeStyle = g;
      ctx.beginPath();
      ctx.moveTo(head.x + o.x, head.y + o.y);
      for (let i = 1; i < pts.length; i++) {
        const q = pts[i];
        if (q) ctx.lineTo(q.x + o.x, q.y + o.y);
      }
      ctx.stroke();
    }
    ctx.globalAlpha = 1;

    // Faint glow. Plain alpha blending, not additive, so bunched trains stay
    // readable instead of adding up to a white blob. None for a scheduled
    // train (HOLLOW_RING).
    for (const { t, p, a, color } of list) {
      if (a < 0.02 || t.pred.scheduled === true) continue;
      const size = 22 * s;
      ctx.globalAlpha = a * 0.4;
      ctx.drawImage(sprite(color), p.x - size / 2, p.y - size / 2, size, size);
    }
    ctx.globalAlpha = 1;

    // Trains: rounded pills in the line's color with an ink ring that cuts a
    // gap in the line beneath, and a small chevron showing direction (none
    // once stopped at a station). Zoomed in, each line also has two tracks
    // and the train rides its own (left of its heading, or right with keepRight).
    for (let i = ghosts.length - 1; i >= 0; i--) {
      const gh = ghosts[i];
      if (!gh) continue;
      gh.a -= dt / SNAP_FADE_MS;
      if (gh.a < 0.02) {
        ghosts.splice(i, 1);
        continue;
      }
      if (hidden.has(gh.lineId)) continue;
      const dim = focus !== null && focus !== '' && focus !== gh.lineId ? 0.12 : 1;
      drawPill(gh.x * scale + o.x, gh.y * scale + o.y, gh.ang, gh.a * dim, gh.color, len, wid, s, false, gh.hollow);
    }
    // Minor lines' vehicles, under the major lines' trains: a dot in the
    // line's color, ringed in ink, or a tag with its route's number zoomed
    // right in. A tag that would cover one already placed stays a dot. A
    // scheduled vehicle's dot and tag are hollow (HOLLOW_RING).
    const tags = z >= MINOR.BULLET_Z ? placeTags(minor, z) : new Map<TrackedTrain, Tag>();
    for (const { t, p, a, color } of minor) {
      if (a < 0.02 || tags.has(t)) continue;
      const hollow = t.pred.scheduled === true;
      ctx.globalAlpha = a;
      ctx.beginPath();
      ctx.arc(p.x, p.y, MINOR.DOT_RADIUS * s, 0, Math.PI * 2);
      ctx.fillStyle = hollow ? INK : color;
      ctx.fill();
      ctx.lineWidth = hollow ? Math.max(1.4, 1.3 * s) : Math.max(1, 1.1 * s);
      ctx.strokeStyle = hollow ? hollowInk(color) : isDark(color) ? CASING : INK;
      ctx.stroke();
    }
    for (const { t, p, a, color } of minor) {
      const tag = tags.get(t);
      if (tag) drawTag(p.x, p.y, a, color, tag, undefined, t.pred.scheduled === true);
    }
    ctx.globalAlpha = 1;
    // Moving trains, then station dots, then trains stopped at a station on
    // top: a train passing through goes under the dot, one at the platform
    // stays visible. Without our own dots (stationDots off) the map's dots
    // are under every train, and only the rings are drawn here.
    // Stopped trains are drawn on top, so their bullets are placed first.
    let bullets = new Map<TrackedTrain, Tag>();
    if (z >= BULLET_Z) {
      const stopped: typeof list = [];
      const moving: typeof list = [];
      for (const v of list) (atStation(v.pos) ? stopped : moving).push(v);
      bullets = placeBullets([...stopped, ...moving], wid);
    }
    for (const { t, pos, p, a, color } of list) {
      if (a < 0.02 || atStation(pos)) continue;
      drawPill(p.x, p.y, t.render.ang, a, color, len, wid, s, false, t.pred.scheduled === true, bullets.get(t));
    }
    ctx.globalAlpha = 1;
    if (z >= 11) {
      if (stationDots) drawDots(sc, net, z, o);
      // A ring in each stopped train's line color, flush against the dot's
      // outline.
      // Station id -> line color -> opacity.
      const rings = new Map<StationId, Map<string, number>>();
      for (const { pos, a, color } of list) {
        if (a < 0.02 || !atStation(pos) || !sc.visibleStations.has(pos.N)) continue;
        let colors = rings.get(pos.N);
        if (!colors) rings.set(pos.N, (colors = new Map<string, number>()));
        colors.set(color, Math.max(colors.get(color) ?? 0, a));
      }
      const ringWidth = 2.2;
      ctx.lineWidth = ringWidth;
      for (const [id, colors] of rings) {
        const st = net.stations.get(id);
        if (!st) throw new TypeError(`no station ${id}`);
        const p = stationPoint(net, st, scale, o);
        let r = stationRadius(z, isInterchange(st, lines)) + stationLine(z) / 2 + ringWidth / 2;
        for (const [color, a] of colors) {
          ctx.globalAlpha = a;
          ctx.strokeStyle = isDark(color) ? CASING : color;
          ctx.beginPath();
          ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
          ctx.stroke();
          r += ringWidth;
        }
      }
      ctx.globalAlpha = 1;
    }
    for (const { t, pos, p, a, color } of list) {
      if (a < 0.02 || !atStation(pos)) continue;
      drawPill(p.x, p.y, t.render.ang, a, color, len, wid, s, true, t.pred.scheduled === true, bullets.get(t));
    }
    ctx.globalAlpha = 1;

    // ?debug: dot every train that should be between stations, green if it
    // moved this frame and red if it's frozen.
    if (debug) {
      for (const { t, p } of list) {
        const v = t.motion.pos;
        if (!v || v.P === null || v.P === '' || t.pred.waiting || v.f >= 0.999) continue;
        ctx.beginPath();
        ctx.arc(p.x, p.y, Math.max(2.5, 2.2 * s), 0, Math.PI * 2);
        ctx.fillStyle = t.motion.moving === true ? '#35E27A' : '#FF3B3B';
        ctx.fill();
        ctx.lineWidth = 1;
        ctx.strokeStyle = INK;
        ctx.stroke();
      }
    }

    // Selection ring.
    const t = sc.selectedId === null ? undefined : sc.trains.get(sc.selectedId);
    if (t?.render.px && !hidden.has(t.pred.lineId)) {
      const pulse = options.reduceMotion === true ? 0 : (Math.sin(clock() / 320) + 1) / 2;
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = 1.6;
      ctx.globalAlpha = 0.9;
      ctx.beginPath();
      ctx.arc(t.render.px.x, t.render.px.y, (9.5 + pulse * 3) * s + 2, 0, Math.PI * 2);
      ctx.stroke();
      ctx.globalAlpha = 1;
    }
  }

  // The drawn train nearest a container point, within radius px.
  function pickTrain(pt: ScreenPoint, radius = 14): TrackedTrain | null {
    if (map.getZoom() < VEHICLE_Z) return null;
    const { trains, hidden } = scene();
    let best: TrackedTrain | null = null;
    let bd = radius;
    for (const t of trains.values()) {
      const px = t.render.px;
      // Not a train fading in (it's where it will be, not yet visible there).
      if (!px || t.dead || (t.render.shown ?? 0) < PICK_ALPHA || hidden.has(t.pred.lineId)) continue;
      const d = Math.hypot(px.x - pt.x, px.y - pt.y);
      if (d < bd) {
        bd = d;
        best = t;
      }
    }
    return best;
  }

  // Safari can restore a tab with stale canvas backing stores. Recheck the
  // map's size and repaint once visible layout has settled.
  function resume(): void {
    requestFrame(() => {
      if (isHidden()) return;
      map.resize();
      resize();
      draw();
    });
  }

  return {
    resize,
    draw,
    pickTrain,
    resume,
  };
}

export {
  CASING,
  CULL,
  INK,
  SPLIT_Z,
  SPLIT_FROM,
  SPLIT_FULL,
  create,
  isDark,
  trainScale,
  railGap,
  stationRadius,
  stationLine,
  isInterchange,
};
